import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

/**
 * PM Spec V3 13/05/2026 — ObservationTemplates
 * 5 phrases pré-cochées modifiables. Cases cochées concaténées dans `value`.
 * Bouton 🎤 vocal pour dictée libre (Web Speech Recognition).
 *
 * type='loading' → phrases chargement
 * type='unloading' → phrases déchargement
 */
const TEMPLATES_BY_TYPE = {
  loading: [
    'Chargement effectué dans les délais prévus.',
    'Bâchage et sécurisation conformes.',
    'Marchandise empilée selon les règles.',
    'Aucune anomalie constatée.',
    'Personnel client présent et coopératif.',
  ],
  unloading: [
    'Livraison effectuée dans les délais.',
    'Marchandise déchargée et comptée avec le client.',
    'Aucune réserve formulée.',
    'Lieu de livraison conforme à l\'adresse prévue.',
    'Personnel destinataire présent.',
  ],
};

const DEFAULT_CHECKED = [0, 1, 3]; // 3 cases positives par défaut

const ObservationTemplates = ({
  type = 'loading',
  value = '',
  onChange,
  testId = 'observation-templates',
}) => {
  const templates = TEMPLATES_BY_TYPE[type] || TEMPLATES_BY_TYPE.loading;
  const [checked, setChecked] = useState(DEFAULT_CHECKED);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const [listening, setListening] = useState(false);

  // Compute concatenated value from checked indices
  useEffect(() => {
    if (customMode) {
      onChange?.(customText);
      return;
    }
    const phrase = checked.map(i => templates[i]).filter(Boolean).join(' ');
    onChange?.(phrase);
  }, [checked, customMode, customText, onChange, templates]);

  const toggle = (i) => {
    setChecked((prev) => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort());
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'fr-FR';
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onstart = () => setListening(true);
    r.onresult = (ev) => {
      const txt = Array.from(ev.results).map(x => x[0].transcript).join(' ');
      setCustomText((prev) => (prev ? prev + ' ' : '') + txt);
      setCustomMode(true);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    try { r.start(); } catch { setListening(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {!customMode && (
        <div className="space-y-2">
          {templates.map((t, i) => (
            <label
              key={i}
              data-testid={`${testId}-row-${i}`}
              className={`flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition-all ${
                checked.includes(i)
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={checked.includes(i)}
                onChange={() => toggle(i)}
                className="mt-0.5 w-5 h-5 accent-emerald-600 cursor-pointer"
                data-testid={`${testId}-checkbox-${i}`}
              />
              <span className={`text-sm leading-relaxed ${checked.includes(i) ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
                {t}
              </span>
            </label>
          ))}
        </div>
      )}

      {customMode && (
        <Textarea
          data-testid={`${testId}-custom`}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder="Tapez ou dictez votre observation libre…"
          className="min-h-[100px] text-sm"
        />
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-toggle-custom`}
          onClick={() => setCustomMode((m) => !m)}
          className="gap-1.5"
        >
          <Pencil className="w-3.5 h-3.5" />
          {customMode ? 'Revenir aux phrases types' : 'Texte libre'}
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-mic`}
          onClick={listening ? undefined : startVoice}
          disabled={listening}
          className={`gap-1.5 ${listening ? 'bg-red-50 border-red-300 text-red-700' : ''}`}
        >
          {listening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
          {listening ? 'Écoute…' : 'Dicter'}
        </Button>
      </div>
    </div>
  );
};

export default ObservationTemplates;
